import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import { getBoletaSettings, updateBoletaSettings, type BoletaSettings } from "@/lib/api";

/**
 * Textos del pie de la boleta.
 *
 * Son los que el socio lee cuando tiene una duda o un problema: donde pagar, a
 * quien llamar, hasta cuando. Lo que se deje vacio no se imprime, asi que el
 * comite completa solo lo que le sirve.
 */

type Campo = {
  clave: keyof BoletaSettings;
  etiqueta: string;
  ayuda?: string;
  ejemplo?: string;
};

const GRUPOS: Array<{ titulo: string; descripcion: string; campos: Campo[] }> = [
  {
    titulo: "Horarios",
    descripcion: "Cuándo puede venir el socio a pagar o a consultar.",
    campos: [
      { clave: "cashDeskHours", etiqueta: "Horario de caja", ejemplo: "Lunes a viernes 09:00 a 14:00" },
      { clave: "officeHours", etiqueta: "Horario de atención", ejemplo: "Lunes a viernes 09:00 a 16:00" },
    ],
  },
  {
    titulo: "Contacto",
    descripcion: "A quién llamar. Van tal cual en la boleta, así que conviene incluir el número.",
    campos: [
      { clave: "customerServiceText", etiqueta: "Atención al cliente", ejemplo: "Atención cliente +56 9 5390 7106" },
      { clave: "emergencyText", etiqueta: "Emergencias", ejemplo: "Emergencias +56 9 8774 8828" },
      { clave: "whatsappText", etiqueta: "WhatsApp", ejemplo: "WhatsApp +56 9 ..." },
      {
        clave: "readingPhotoPhone",
        etiqueta: "Envío de foto de lectura",
        ejemplo: "Foto de lectura al +56 9 2365 1022",
      },
    ],
  },
  {
    titulo: "Cómo pagar y consultar",
    descripcion: "Dónde puede pagar el socio y dónde revisa su deuda.",
    campos: [
      {
        clave: "onlinePaymentText",
        etiqueta: "Consulta de deuda",
        ayuda: "Se imprime junto con la dirección web de abajo.",
        ejemplo: "Consulta tu deuda con tu RUT en",
      },
      { clave: "debtInquiryUrl", etiqueta: "Dirección web", ejemplo: "https://aprquechurehue.cl/pagar" },
      { clave: "totemText", etiqueta: "Puntos de pago presencial", ejemplo: "Tótem de pago en Av. ..." },
      {
        clave: "bankAccountText",
        etiqueta: "Datos de transferencia",
        ayuda: "Aparece impreso en cada boleta. Pon solo lo que quieras que sea público.",
        ejemplo: "Cta. Vista 000000 Banco ... a nombre de ...",
      },
    ],
  },
  {
    titulo: "Aviso de corte",
    descripcion: "El día sale de Sistema → Día corte. Acá solo se cambia cómo se anuncia.",
    campos: [
      {
        clave: "cutNoticeLabel",
        etiqueta: "Texto del aviso",
        ayuda: "Se le agrega la fecha automáticamente. Si lo dejas vacío se usa el texto por defecto.",
        ejemplo: "CORTE EN TRÁMITE - FECHA CORTE",
      },
    ],
  },
];

export default function BoletaTab() {
  const { session } = useSession();
  const [datos, setDatos] = useState<BoletaSettings | null>(null);
  const [notas, setNotas] = useState("");
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    getBoletaSettings(session)
      .then((r) => {
        setDatos(r);
        setNotas((r.extraNotes ?? []).join("\n"));
      })
      .catch(() => setError("No se pudo cargar la configuración de la boleta"))
      .finally(() => setCargando(false));
  }, [session]);

  async function guardar() {
    if (!session || !datos) return;
    setGuardando(true);
    setError(null);
    setGuardado(false);
    try {
      const actualizado = await updateBoletaSettings(session, {
        ...datos,
        extraNotes: notas
          .split("\n")
          .map((linea) => linea.trim())
          .filter(Boolean),
      });
      setDatos(actualizado);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!datos) {
    return <div className="text-sm text-red-600">{error ?? "No se pudo cargar"}</div>;
  }

  const input =
    "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500";

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-lg font-semibold text-gray-900">Textos de la boleta</h2>
        <p className="text-sm text-gray-500 mt-1">
          Es lo que el socio lee cuando tiene una duda: dónde pagar, a quién llamar, hasta cuándo.
          Lo que dejes vacío no se imprime.
        </p>
      </div>

      {GRUPOS.map((grupo) => (
        <div key={grupo.titulo} className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800">{grupo.titulo}</h3>
          <p className="text-sm text-gray-500 mt-0.5 mb-4">{grupo.descripcion}</p>
          <div className="grid md:grid-cols-2 gap-4">
            {grupo.campos.map((campo) => (
              <div key={String(campo.clave)}>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{campo.etiqueta}</label>
                <input
                  value={(datos[campo.clave] as string | null) ?? ""}
                  onChange={(e) => setDatos({ ...datos, [campo.clave]: e.target.value })}
                  placeholder={campo.ejemplo}
                  className={input}
                />
                {campo.ayuda && <p className="text-xs text-gray-400 mt-1">{campo.ayuda}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800">Avisos adicionales</h3>
        <p className="text-sm text-gray-500 mt-0.5 mb-4">
          Una línea por aviso. Sirve para cosas del momento, como un cambio de horario o un corte
          programado por mantención.
        </p>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          rows={4}
          placeholder={"Desde enero no se atiende los sábados\nSolo pagos por el total de la deuda"}
          className={`${input} resize-none`}
        />
      </div>

      {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button
          onClick={guardar}
          disabled={guardando}
          className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {guardando ? "Guardando..." : "Guardar cambios"}
        </button>
        {guardado && <span className="text-sm text-emerald-600">Guardado</span>}
      </div>
    </div>
  );
}
